import React from 'react'
import { Link } from 'react-router-dom'
import { useI18nContext } from '../hooks/I18nContext'

const About: React.FC = () => {
  const { t, tRaw } = useI18nContext()

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-blue-50 to-green-50">
      <div className="relative z-10">
        <main className="pt-20">
          {/* Hero Section */}
          <section className="py-20 px-4">
            <div className="max-w-6xl mx-auto text-center">
              <h1 className="text-5xl md:text-7xl font-bold text-gray-800 mb-6">
                {t('about.hero.title')}
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
                {t('about.hero.subtitle')}
              </p>
            </div>
          </section>

          {/* Mission & Vision */}
          <section className="py-16 px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h2 className="text-3xl font-bold text-gray-800 mb-6">
                    {t('about.mission.title')}
                  </h2>
                  <p className="text-lg text-gray-600">
                    {t('about.mission.description')}
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h2 className="text-3xl font-bold text-gray-800 mb-6">
                    {t('about.vision.title')}
                  </h2>
                  <p className="text-lg text-gray-600">
                    {t('about.vision.description')}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Core Team */}
          <section className="py-16 px-4 bg-white">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">
                {t('about.team.title')}
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {(tRaw('about.teamMembers') || []).map((member: any, index: number) => (
                  <div key={index} className="bg-gray-50 rounded-2xl p-6 text-center">
                    <div className="text-4xl mb-4">{member.avatar}</div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                      {member.name}
                    </h3>
                    <p className="text-pink-500 font-semibold mb-3">
                      {member.role}
                    </p>
                    <p className="text-gray-600">
                      {member.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Core Values */}
          <section className="py-16 px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">
                {t('about.value.title')}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {(tRaw('about.values') || []).map((value: any, index: number) => (
                  <div key={index} className="bg-white rounded-2xl p-6 shadow-lg text-center">
                    <div className="text-4xl mb-4">{value.icon}</div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      {value.title}
                    </h3>
                    <p className="text-gray-600">
                      {value.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Development History */}
          <section className="py-16 px-4 bg-white">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">
                {t('about.history.title')}
              </h2>
              <div className="space-y-8">
                {(tRaw('about.timeline') || []).map((milestone: any, index: number) => (
                  <div key={index} className="flex items-center bg-gray-50 rounded-2xl p-6">
                    <div className="w-20 h-20 bg-pink-500 rounded-full flex items-center justify-center mr-6">
                      <span className="text-2xl font-bold text-white">{milestone.year}</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-2">
                        {milestone.title}
                      </h3>
                      <p className="text-gray-600">
                        {milestone.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Partners */}
          <section className="py-16 px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">
                {t('about.partner.title')}
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {(tRaw('about.partners') || []).map((partner: any, index: number) => (
                  <div key={index} className="bg-white rounded-2xl p-6 shadow-lg text-center">
                    <div className="text-4xl mb-4">{partner.logo}</div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      {partner.name}
                    </h3>
                    <p className="text-gray-600">
                      {partner.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="py-16 px-4 bg-white">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">
                {t('about.contact.title')}
              </h2>
              
              {/* Contact Info Cards */}
              <div className="grid md:grid-cols-2 gap-8 mb-12">
                <div className="bg-gray-50 rounded-2xl p-6 text-center">
                  <div className="text-3xl mb-4">📧</div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">{t('about.contactInfo.email')}</h3>
                  <p className="text-gray-600">contact@genius-ai.com</p>
                </div>
                <div className="bg-gray-50 rounded-2xl p-6 text-center">
                  <div className="text-3xl mb-4">📞</div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">{t('about.contactInfo.phone')}</h3>
                  <p className="text-gray-600">+62-21-1234-5678</p>
                </div>
              </div>

              {/* Google Maps */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="p-6">
                  <h3 className="text-2xl font-semibold text-gray-800 mb-4 text-center">
                    {t('about.contactInfo.location')}
                  </h3>
                  <div className="relative w-full h-96 rounded-lg overflow-hidden">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3966.123456789!2d106.8915818!3d-6.1739496!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2e69f5275a5a29bf%3A0x6295678aa123a43e!2sKirana%20Three%20Office%20Tower!5e0!3m2!1sen!2sid!4v1234567890123!5m2!1sen!2sid"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="GENIUS AI Office Location"
                    ></iframe>
                  </div>
                  <div className="mt-4 text-center">
                    <a
                      href="https://www.google.com/maps/place/Kirana+Three+Office+Tower/@-6.1739496,106.8915818,17z/data=!3m1!4b1!4m6!3m5!1s0x2e69f5275a5a29bf:0x6295678aa123a43e!8m2!3d-6.1739496!4d106.8941567!16s%2Fg%2F11f0x32myw?entry=ttu&g_ep=EgoyMDI1MDkxNi4wIKXMDSoASAFQAw%3D%3D"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      <span className="mr-2">🗺️</span>
                      {t('about.contactInfo.viewOnMaps')}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-16 px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-4xl font-bold text-gray-800 mb-6">
                {t('about.cta.title')}
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                {t('about.cta.description')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/buy"
                  className="bg-pink-500 text-white py-3 px-8 rounded-lg text-lg font-semibold hover:bg-pink-600 transition-colors"
                >
                  {t('about.cta.buttonPrimary')}
                </Link>
                <Link
                  to="/careers"
                  className="bg-white text-pink-500 py-3 px-8 rounded-lg text-lg font-semibold border-2 border-pink-500 hover:bg-pink-50 transition-colors"
                >
                  {t('about.cta.buttonSecondary')}
                </Link>
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  )
}

export default About
